import { parseSecretManagerField } from "@p0security/integrations-vaulted-credential-shared";
import { assertNever } from "@p0security/util/assertNever";

import type { ClickHouseOrganizationConfig } from "./types.ts";

/** Where an organization's API key is, in the customer's own secret manager:
 * the pointer P0 gives the connector on every call, as the connector's
 * `secretPointer` schema (p0-connector-clickhouse) takes it. The secret holds
 * the key as JSON, `{"keyId": "...", "keySecret": "..."}`. P0 never holds
 * it. */
export type ApiKeySecret =
  | { manager: "aws"; region: string; secretId: string }
  | { manager: "gcp"; projectId: string; secretId: string };

/**
 * The partial ARN of the AWS Secrets Manager secret `name`, in `accountId` and
 * `region`: the secret's ARN without the hyphen and six random characters that
 * Secrets Manager adds to its name.
 *
 * Secrets Manager finds a secret by its partial ARN from another account too,
 * which a bare name doesn't allow. The organization's Terraform grants read on
 * it followed by `-??????`. It trims each value, as P0 stores it. P0 takes
 * only regions in AWS's commercial partition, so the partition is `aws`.
 *
 * It copies Auth0's helper instead of sharing it, so adding ClickHouse Cloud
 * changes no other integration.
 */
const toSecretPartialArn = ({
  accountId,
  name,
  region,
}: {
  accountId: string;
  name: string;
  region: string;
}) =>
  `arn:aws:secretsmanager:${region.trim()}:${accountId.trim()}:secret:${name.trim()}`;

/**
 * Points the connector at the secret that holds an organization's API key: by
 * its partial ARN on AWS, and by its name and project on Google Cloud.
 *
 * It trims each value, as P0 stores it, because install checks see the item
 * as the request left it.
 */
export const toApiKeySecret = ({
  apiKeySecretName,
  vault,
}: Pick<
  ClickHouseOrganizationConfig,
  "apiKeySecretName" | "vault"
>): ApiKeySecret => {
  const locator = parseSecretManagerField(vault);
  const name = apiKeySecretName.trim();
  switch (locator.provider) {
    case "aws":
      return {
        manager: "aws",
        region: locator.region.trim(),
        secretId: toSecretPartialArn({
          accountId: locator.accountId,
          name,
          region: locator.region,
        }),
      };
    case "gcp":
      return {
        manager: "gcp",
        projectId: locator.projectId.trim(),
        secretId: name,
      };
    default:
      throw assertNever(locator);
  }
};
